import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from 'admin/common/icons/CloseIcon';
import Apiauth from 'API/Apiauth';
import aibirdeyeIcon from '../../assets/images/aibirdeye.svg';
import {
  buildRefillReplyFromSoldRefill,
  EDIT_MODE_BASE_CHIPS,
  getAiActionChips,
  getSoldRefillLines,
  getSoldRefillStatusMessage,
  getUnmatchedLines,
  getUnmatchedReplaceStatusMessage,
  hasAiSuggestions,
  getChatProductKey,
  isChatProductShowingAsAdded,
  enrichReplaceCurrentItemProducts,
  enrichReplaceUnmatchedProducts,
  getIssueUnmatchedPoProductIds,
  hasSoldRefillSuggestions,
  normalizeAiChatProducts,
  ISSUE_SHORTAGE_INTENT,
  isReplaceProductIntent,
  PRESET_COMPOSER_HINT_PRESETS,
  REPLACE_CURRENT_ITEM_INTENT,
  resolveChatProductIntent,
  SUGGEST_SIMILAR_ITEMS_PRESET,
  soldRefillItemsToChatProducts,
} from './zoneAiSuggestionsUtils';
import {
  buildIssueShortageAlertLines,
  buildIssueShortageSuggestPayload,
} from './zoneIssueShortageUtils';
import {
  buildInputWithFieldValue,
  buildInputWithSlashField,
  filterSlashMenuOptions,
  getIssueFieldValueCounts,
  getIssueFieldValueSuggestions,
  getSearchFieldValueMenuState,
  getSlashMenuState,
  isIssueFieldValuePickerField,
  ZONE_CHAT_SEARCH_FIELDS,
} from './zoneChatComposerUtils';
import {
  CHAT_STREAM_CHARS_PER_TICK,
  CHAT_STREAM_TICK_MS,
  getNextStreamSlice,
  normalizeChatMessageText,
} from './zoneChatStreamUtils';
import './ZoneChatWidget.scss';

const MIN_LOADER_DURATION_MS = 1000;

const INITIAL_VISIBLE_MESSAGES = 2;
const MESSAGES_PAGE_SIZE = 2;
const LOAD_MORE_SCROLL_THRESHOLD = 24;
const LOAD_OLDER_DELAY_MS = 1000;

const withMinLoaderDelay = async (
  promise,
  minMs = MIN_LOADER_DURATION_MS
) => {
  const start = Date.now();
  const result = await promise;
  const elapsed = Date.now() - start;
  if (elapsed < minMs) {
    await new Promise((resolve) => setTimeout(resolve, minMs - elapsed));
  }
  return result;
};

const ChatStreamCursor = () => (
  <span className="zone-chat-widget__stream-cursor" aria-hidden />
);

const ChatLoadingMessage = ({ text }) => (
  <div className="zone-chat-widget__message zone-chat-widget__message--loading">
    <span
      className="zone-chat-widget__loading-spinner"
      aria-hidden
    />
    <span className="zone-chat-widget__loading-text">{text}</span>
  </div>
);

const DEFAULT_CHIPS = [
  {
    id: 'replace',
    labelKey: 'Replace current item',
    highlighted: false,
    showAiBadge: true,
  },
  {
    id: 'suggest',
    labelKey: 'Suggest similar items',
    highlighted: false,
    showAiBadge: true,
  },
];

const ChipIconReplace = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M14.5 6.5L12 4M12 4L14.5 1.5M12 4H8C5.79086 4 4 5.79086 4 8V9M5.5 13.5L8 16M8 16L5.5 18.5M8 16H12C14.2091 16 16 14.2091 16 12V11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChipIconSearch = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M13 13L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ChipIconRemove = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <circle cx="10" cy="10" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7 10H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ChipIconRefill = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M10 3V11M10 11L7 8M10 11L13 8M4 14H16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CHIP_ICONS = {
  replace: ChipIconReplace,
  suggest: ChipIconSearch,
  replace_unmatched: ChipIconReplace,
  remove_unmatched: ChipIconRemove,
  refill_sold: ChipIconRefill,
};

const ChipAiBadge = () => (
  <span className="zone-chat-widget__chip-ai" aria-hidden>
    <img src={aibirdeyeIcon} alt="" />
  </span>
);

const REMOVE_UNDO_SECONDS = 10;
const AI_SUGGESTIONS_MSG_ID = 'ai-suggestions-intro';
const ZONE_CHAT_PRODUCT_PLACEHOLDER =
  'https://gis247.s3.us-east-2.amazonaws.com/placeholder-blank.png';

const ChipIconPlus = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M10 4V16M4 10H16"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
    />
  </svg>
);

const ChipIconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M2.5 6L5 8.5L9.5 3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ComposerSendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
    <path
      d="M3 9H15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M9 3L15 9L9 15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const COMPOSER_STOP_SQUARE_SIZE = 13;

const ComposerStopIcon = () => (
  <span
    className="zone-chat-widget__composer-stop"
    aria-hidden
    style={{
      width: COMPOSER_STOP_SQUARE_SIZE,
      height: COMPOSER_STOP_SQUARE_SIZE,
    }}
  />
);

const ReplaceCurrentItemPromptContent = ({ t }) => (
  <div className="zone-chat-widget__replace-prompt">
    <p>{t('Sure. What would you like to replace?')}</p>
    <p>{t('Enter any of the following:')}</p>
    <ul>
      <li>{t('SKU')}</li>
      <li>{t('Item type (e.g. ring, necklace)')}</li>
      <li>{t('Collection')}</li>
      <li>{t('Stock ID')}</li>
    </ul>
    <p>
      {t("I'll find matching items and suggest replacements.", {
        defaultValue: "I'll find matching items and suggest replacements.",
      })}
    </p>
  </div>
);

const SuggestSimilarItemsPromptContent = ({ t }) => (
  <div className="zone-chat-widget__replace-prompt">
    <p>{t('What would you like to find similar items for?')}</p>
    <p>{t('Enter any of the following:')}</p>
    <ul>
      <li>{t('SKU')}</li>
      <li>{t('Item type (e.g. ring, necklace)')}</li>
      <li>{t('Collection')}</li>
      <li>{t('Stock ID')}</li>
    </ul>
    <p>{t('Add how many results you want (optional).')}</p>
    <p>
      {t("I'll suggest matching items based on similar details.", {
        defaultValue: "I'll suggest matching items based on similar details.",
      })}
    </p>
  </div>
);

const IssueShortagePromptContent = ({
  alertData,
  resolved,
  onYes,
  onNo,
  t,
}) => {
  const lines = buildIssueShortageAlertLines(alertData, t);

  return (
    <div className="zone-chat-widget__issue-shortage-prompt">
      <p>{lines.previousLine}</p>
      <p>{lines.currentLine}</p>
      <p className="zone-chat-widget__issue-shortage-question">
        {lines.questionLine}
      </p>
      {!resolved ? (
        <div className="zone-chat-widget__issue-shortage-actions">
          <button
            type="button"
            className="zone-chat-widget__issue-shortage-btn"
            onClick={onYes}
          >
            {t('Yes')}
          </button>
          <button
            type="button"
            className="zone-chat-widget__issue-shortage-btn"
            onClick={onNo}
          >
            {t('No')}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const RefillProductCard = ({
  product,
  isAdded,
  isAdding,
  onAdd,
  actionTitle,
  isReplaceAction = false,
  t,
}) => {
  const handleImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = ZONE_CHAT_PRODUCT_PLACEHOLDER;
  };

  return (
    <article
      className={`zone-chat-widget__product-card${
        isAdded ? ' zone-chat-widget__product-card--added' : ''
      }`}
    >
      <div className="zone-chat-widget__product-card-image">
        <img
          src={product?.main_image || ZONE_CHAT_PRODUCT_PLACEHOLDER}
          alt={product?.SKU || ''}
          onError={handleImageError}
        />
      </div>
      <div className="zone-chat-widget__product-card-footer">
        <div className="zone-chat-widget__product-card-meta">
          <span className="zone-chat-widget__product-card-sku">
            {product?.SKU || '-'}
          </span>
          <span className="zone-chat-widget__product-card-stock-id">
            {product?.stock_id || '-'}
          </span>
        </div>
        {isAdded ? (
          <span
            className="zone-chat-widget__product-card-added"
            aria-label={t('Added to issue')}
            title={t('Added to issue')}
          >
            <ChipIconCheck />
          </span>
        ) : (
          <button
            type="button"
            className={`zone-chat-widget__product-card-add${
              isReplaceAction ? ' zone-chat-widget__product-card-add--replace' : ''
            }`}
            onClick={() => onAdd?.(product)}
            disabled={isAdding}
            aria-label={actionTitle}
            title={actionTitle}
            data-tooltip={isReplaceAction ? actionTitle : undefined}
          >
            <ChipIconPlus />
          </button>
        )}
      </div>
    </article>
  );
};

const NoUnmatchedItemsMessage = ({ zoneName, t }) => (
  <>
    {zoneName ? (
      <p className="zone-chat-widget__suggestions-heading">
        {t('AI suggestions for {{zone}}.', { zone: zoneName })}
      </p>
    ) : null}
    <p className="zone-chat-widget__suggestions-status">
      {t('No unmatched items identified.')}
    </p>
    <p className="zone-chat-widget__suggestions-status">
      {t('This zone is already well organized.')}
    </p>
  </>
);

const isAiSuggestionEntryMessageId = (messageId) =>
  String(messageId || '').startsWith('ai-suggestions-') &&
  messageId !== AI_SUGGESTIONS_MSG_ID;

const AssistantSuggestionsContent = ({
  zoneName,
  soldLines,
  unmatchedLines,
  soldStatusMessage = '',
  unmatchedStatusMessage = '',
  t,
}) => {
  const hasSoldContent = soldLines.length > 0 || Boolean(soldStatusMessage);
  const hasUnmatchedContent = unmatchedLines.length > 0;

  if (!hasSoldContent && !hasUnmatchedContent) {
    return (
      <div className="zone-chat-widget__suggestions zone-chat-widget__suggestions--no-unmatched">
        <NoUnmatchedItemsMessage zoneName={zoneName} t={t} />
      </div>
    );
  }

  return (
    <div className="zone-chat-widget__suggestions">
      <p className="zone-chat-widget__suggestions-intro">
        {t('AI suggestions for {{zone}}.', { zone: zoneName })}
      </p>

      {soldLines.length > 0 ? (
        <section className="zone-chat-widget__suggestions-section">
          <p className="zone-chat-widget__suggestions-heading">
            {t('Suggested replacements for sold items:')}
          </p>
          <ul className="zone-chat-widget__suggestions-list">
            {soldLines.map((line, index) => (
              <li key={`sold-${line.soldSku}-${index}`}>
                <span className="zone-chat-widget__sku">{line.soldSku}</span>
                <span className="zone-chat-widget__arrow" aria-hidden>
                  →
                </span>
                <span className="zone-chat-widget__sku">{line.replacementSku}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : soldStatusMessage ? (
        <section className="zone-chat-widget__suggestions-section">
          <p className="zone-chat-widget__suggestions-heading">
            {t('Sold items:')}
          </p>
          <p className="zone-chat-widget__suggestions-status">{soldStatusMessage}</p>
        </section>
      ) : null}

      {hasUnmatchedContent ? (
        <section className="zone-chat-widget__suggestions-section">
          <p className="zone-chat-widget__suggestions-heading">
            {t('Unmatched items identified:')}
          </p>
          <ul className="zone-chat-widget__suggestions-list zone-chat-widget__suggestions-list--unmatched">
            {unmatchedLines.map((line) => (
              <li key={line.sku}>
                <span className="zone-chat-widget__sku">{line.sku}</span>
                <span className="zone-chat-widget__dash" aria-hidden>
                  —
                </span>
                <span className="zone-chat-widget__reason">{line.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : !hasSoldContent ? (
        <section className="zone-chat-widget__suggestions-section zone-chat-widget__suggestions-section--no-unmatched">
          <NoUnmatchedItemsMessage zoneName={zoneName} t={t} />
        </section>
      ) : null}
    </div>
  );
};

export default function ZoneChatWidget({
  aiSuggestions,
  issueRows = [],
  isEditMode = false,
  isChatEnabled = true,
  zoneId,
  locationId,
  issueId,
  zoneLabel,
  hasNotification = false,
  issueShortagePrompt = null,
  onIssueShortageYes,
  onIssueShortageNo,
  onStartRemoveUnmatched,
  onCommitRemoveUnmatched,
  onUndoRemoveUnmatched,
  onAddChatProduct,
  hideComposerAndPresets = false,
  disableChatSessionControls = false,
  appendAiSuggestionsEntries = [],
  panelTitle,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [userName, setUserName] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(null);
  const [activeUndoMessageId, setActiveUndoMessageId] = useState(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isComposerSending, setIsComposerSending] = useState(false);
  const [pendingPreset, setPendingPreset] = useState(null);
  const [addedProductKeys, setAddedProductKeys] = useState(() => new Set());
  const [addingProductKey, setAddingProductKey] = useState(null);
  const [inputSelection, setInputSelection] = useState(0);
  const [slashMenuHighlightIndex, setSlashMenuHighlightIndex] = useState(0);
  const [fieldValueMenuHighlightIndex, setFieldValueMenuHighlightIndex] = useState(0);
  const [fieldValueMenuDismissed, setFieldValueMenuDismissed] = useState(false);
  const transcriptEndRef = useRef(null);
  const textareaRef = useRef(null);
  const bodyRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);
  const loadOlderTimerRef = useRef(null);
  const chatMessagesLengthRef = useRef(0);
  const stickToBottomRef = useRef(true);
  const streamTimersRef = useRef({});
  const presetPromptTimerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_MESSAGES);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const clearPresetPromptTimer = useCallback(() => {
    if (presetPromptTimerRef.current) {
      clearTimeout(presetPromptTimerRef.current);
      presetPromptTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearPresetPromptTimer(), [clearPresetPromptTimer]);

  const hasChatContext = Boolean(zoneId && locationId && issueId);

  const showSuggestions = hasAiSuggestions(aiSuggestions);
  const soldLines = useMemo(
    () => getSoldRefillLines(aiSuggestions),
    [aiSuggestions]
  );
  const unmatchedLines = useMemo(
    () => getUnmatchedLines(aiSuggestions),
    [aiSuggestions]
  );
  const actionChips = useMemo(() => {
    const aiChips = showSuggestions
      ? getAiActionChips(aiSuggestions, issueRows, { isEditMode: false })
      : [];

    if (!isEditMode) {
      return aiChips.length ? aiChips : DEFAULT_CHIPS;
    }

    const merged = [...aiChips];
    const existingIds = new Set(merged.map((chip) => chip.id));
    EDIT_MODE_BASE_CHIPS.forEach((chip) => {
      if (!existingIds.has(chip.id)) {
        merged.push(chip);
      }
    });
    return merged.length ? merged : EDIT_MODE_BASE_CHIPS;
  }, [aiSuggestions, issueRows, isEditMode, showSuggestions]);

  const loadUserName = useCallback(async () => {
    try {
      const res = await Apiauth({}, 'App/profile/get-auth');
      if (res?.code === 200) {
        const name = res.data?.fname || res.data?.name || '';
        setUserName(name);
      }
    } catch (err) {
      console.error(err, 'Profile API Error');
    }
  }, []);

  useEffect(() => {
    if (isOpen && !showSuggestions) {
      loadUserName();
    }
  }, [isOpen, loadUserName, showSuggestions]);

  useEffect(() => {
    if (!isChatEnabled && isOpen) {
      setIsOpen(false);
    }
  }, [isChatEnabled, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setPanelMounted(true);
      const frameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPanelVisible(true));
      });
      return () => cancelAnimationFrame(frameId);
    }
    setPanelVisible(false);
    return undefined;
  }, [isOpen]);

  const handlePanelTransitionEnd = (event) => {
    if (event.propertyName !== 'transform') return;
    if (!panelVisible) {
      setPanelMounted(false);
    }
  };

  useEffect(() => {
    if (appendAiSuggestionsEntries.length) return;
    if (!showSuggestions) return;
    setChatMessages((prev) => {
      if (prev.some((message) => message.id === AI_SUGGESTIONS_MSG_ID)) {
        return prev;
      }
      return [{ id: AI_SUGGESTIONS_MSG_ID, type: 'assistant' }, ...prev];
    });
  }, [appendAiSuggestionsEntries.length, showSuggestions]);

  useEffect(() => {
    if (!appendAiSuggestionsEntries.length) {
      if (!disableChatSessionControls) return;

      setChatMessages((prev) =>
        prev.filter((message) => !isAiSuggestionEntryMessageId(message.id))
      );
      return;
    }

    setChatMessages(
      appendAiSuggestionsEntries.map((entry) => ({
        id: `ai-suggestions-${entry.id}`,
        type: 'assistant',
        zoneName: entry.zoneLabel,
        aiSuggestions: entry.data,
      }))
    );
  }, [appendAiSuggestionsEntries, disableChatSessionControls]);

  const issueShortagePromptKey = issueShortagePrompt?.alertKey;

  useEffect(() => {
    if (!issueShortagePrompt || !issueShortagePromptKey) return undefined;

    const messageId = `issue-shortage-${issueShortagePromptKey}`;

    setChatMessages((prev) => {
      if (prev.some((message) => message.id === messageId)) {
        return prev;
      }

      return [
        ...prev.filter(
          (message) =>
            message.type !== 'issue_shortage_prompt' || message.resolved
        ),
        {
          id: messageId,
          type: 'issue_shortage_prompt',
          alertData: issueShortagePrompt,
          resolved: false,
        },
      ];
    });

    return undefined;
  }, [issueShortagePrompt, issueShortagePromptKey]);

  const hasTranscript = chatMessages.length > 0;
  chatMessagesLengthRef.current = chatMessages.length;
  const hasOlderMessages =
    !disableChatSessionControls && chatMessages.length > visibleCount;
  const visibleMessages = useMemo(
    () =>
      disableChatSessionControls || !hasOlderMessages
        ? chatMessages
        : chatMessages.slice(chatMessages.length - visibleCount),
    [chatMessages, disableChatSessionControls, hasOlderMessages, visibleCount]
  );

  const markStickToBottom = useCallback(() => {
    stickToBottomRef.current = true;
  }, []);

  const scrollChatToBottom = useCallback(() => {
    const el = bodyRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const loadOlderMessages = useCallback(() => {
    if (
      disableChatSessionControls ||
      chatMessagesLengthRef.current <= visibleCount ||
      isLoadingOlder ||
      loadOlderTimerRef.current
    ) {
      return;
    }

    stickToBottomRef.current = false;
    setIsLoadingOlder(true);
    loadOlderTimerRef.current = setTimeout(() => {
      const scrollEl = bodyRef.current;
      if (scrollEl) {
        pendingScrollRestoreRef.current = {
          scrollHeight: scrollEl.scrollHeight,
          scrollTop: scrollEl.scrollTop,
        };
      }
      setVisibleCount((prev) =>
        Math.min(
          prev + MESSAGES_PAGE_SIZE,
          chatMessagesLengthRef.current
        )
      );
      setIsLoadingOlder(false);
      loadOlderTimerRef.current = null;
    }, LOAD_OLDER_DELAY_MS);
  }, [disableChatSessionControls, isLoadingOlder, visibleCount]);

  const handleBodyScroll = useCallback(
    (event) => {
      const el = event.currentTarget;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom <= 100;

      if (
        !disableChatSessionControls &&
        el.scrollTop <= LOAD_MORE_SCROLL_THRESHOLD
      ) {
        loadOlderMessages();
      }
    },
    [disableChatSessionControls, loadOlderMessages]
  );

  const handleBodyWheel = useCallback(
    (event) => {
      if (disableChatSessionControls || event.deltaY >= 0) return;

      const el = bodyRef.current;
      if (!el || el.scrollTop > LOAD_MORE_SCROLL_THRESHOLD) return;

      loadOlderMessages();
    },
    [disableChatSessionControls, loadOlderMessages]
  );

  useLayoutEffect(() => {
    const el = bodyRef.current;
    const pending = pendingScrollRestoreRef.current;
    if (el && pending) {
      el.scrollTop = el.scrollHeight - pending.scrollHeight + pending.scrollTop;
      pendingScrollRestoreRef.current = null;
    }
  }, [visibleCount]);

  useEffect(
    () => () => {
      if (loadOlderTimerRef.current) {
        clearTimeout(loadOlderTimerRef.current);
        loadOlderTimerRef.current = null;
      }
    },
    []
  );

  useEffect(
    () => () => {
      Object.values(streamTimersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
      streamTimersRef.current = {};
    },
    []
  );

  const clearMessageStream = useCallback((messageId) => {
    const timerId = streamTimersRef.current[messageId];
    if (timerId) {
      clearTimeout(timerId);
      delete streamTimersRef.current[messageId];
    }
  }, []);

  const clearAllMessageStreams = useCallback(() => {
    Object.keys(streamTimersRef.current).forEach((messageId) => {
      clearMessageStream(messageId);
    });
  }, [clearMessageStream]);

  const streamTextOnMessage = useCallback(
    (messageId, fullText, field = 'text') => {
      clearMessageStream(messageId);

      const safeText = String(fullText || '');
      if (!safeText) {
        setChatMessages((prev) =>
          prev.map((message) => {
            if (message.id !== messageId) return message;

            if (field === 'reply') {
              return {
                ...message,
                reply: '',
                isStreamingReply: false,
                showProducts: true,
              };
            }

            return {
              ...message,
              text: '',
              isStreaming: false,
            };
          })
        );
        return;
      }

      let currentLength = 0;

      const tick = () => {
        const { text, isComplete, nextLength } = getNextStreamSlice(
          safeText,
          currentLength,
          CHAT_STREAM_CHARS_PER_TICK
        );
        currentLength = nextLength;

        setChatMessages((prev) =>
          prev.map((message) => {
            if (message.id !== messageId) return message;

            if (field === 'reply') {
              return {
                ...message,
                reply: text,
                isStreamingReply: !isComplete,
                showProducts: isComplete ? true : message.showProducts,
              };
            }

            return {
              ...message,
              text,
              isStreaming: !isComplete,
            };
          })
        );

        if (isComplete) {
          delete streamTimersRef.current[messageId];
          return;
        }

        streamTimersRef.current[messageId] = setTimeout(
          tick,
          CHAT_STREAM_TICK_MS
        );
      };

      tick();
    },
    [clearMessageStream]
  );

  const showGreeting =
    !hideComposerAndPresets && !hasTranscript && !showSuggestions;
  const showChips =
    !hideComposerAndPresets &&
    (isEditMode ||
      (showSuggestions && actionChips.length > 0) ||
      showGreeting);

  useLayoutEffect(() => {
    if (!isOpen || !hasTranscript || !stickToBottomRef.current) return;

    scrollChatToBottom();
  }, [
    chatMessages,
    hasTranscript,
    isChatLoading,
    isOpen,
    scrollChatToBottom,
    undoSecondsLeft,
    visibleMessages,
  ]);

  useEffect(() => {
    if (undoSecondsLeft === null) return undefined;

    if (undoSecondsLeft <= 0) {
      onCommitRemoveUnmatched?.();
      setUndoSecondsLeft(null);
      setActiveUndoMessageId(null);
      return undefined;
    }

    const timerId = setTimeout(() => {
      setUndoSecondsLeft((prev) => (prev === null ? null : prev - 1));
    }, 1000);

    return () => clearTimeout(timerId);
  }, [undoSecondsLeft, onCommitRemoveUnmatched]);

  const appendProductSuggestionsMessage = useCallback(
    (reply, products, options = {}) => {
      const {
        showReply = false,
        intent = 'refill_sold',
        streamReply = true,
      } = options;
      const normalizedReply = normalizeChatMessageText(reply);
      const timestamp = Date.now();
      const messageId = `product-suggestions-${timestamp}`;
      const shouldStream =
        streamReply &&
        showReply &&
        Boolean(String(normalizedReply || '').trim());

      setChatMessages((prev) => [
        ...prev.filter((message) => message.type !== 'loading'),
        {
          id: messageId,
          type: 'refill_products',
          reply: shouldStream ? '' : normalizedReply || '',
          products: products || [],
          showReply,
          showProducts: !shouldStream,
          isStreamingReply: shouldStream,
          intent,
        },
      ]);

      if (shouldStream) {
        requestAnimationFrame(() => {
          streamTextOnMessage(messageId, normalizedReply, 'reply');
        });
      }
    },
    [streamTextOnMessage]
  );

  const appendStreamingSystemMessage = useCallback(
    (fullText, messageId) => {
      const safeText = normalizeChatMessageText(fullText);
      const shouldStream = Boolean(safeText.trim());

      setChatMessages((prev) => [
        ...prev.filter((message) => message.type !== 'loading'),
        {
          id: messageId,
          type: 'system',
          text: shouldStream ? '' : safeText,
          isStreaming: shouldStream,
        },
      ]);

      if (shouldStream) {
        requestAnimationFrame(() => {
          streamTextOnMessage(messageId, safeText, 'text');
        });
      }
    },
    [streamTextOnMessage]
  );

  const handleIssueShortageChoice = useCallback(
    async (messageId, choice) => {
      const timestamp = Date.now();

      setChatMessages((prev) => {
        const updated = prev.map((message) =>
          message.id === messageId
            ? { ...message, resolved: true }
            : message
        );

        if (choice === 'yes') {
          return [
            ...updated,
            {
              id: `sent-shortage-yes-${timestamp}`,
              type: 'sent',
              text: t('Yes'),
            },
          ];
        }

        return updated;
      });

      if (choice === 'no') {
        onIssueShortageNo?.();
        return;
      }

      onIssueShortageYes?.();

      if (!zoneId || !locationId) {
        appendStreamingSystemMessage(
          t('Unable to load shortage suggestions. Issue context is missing.'),
          `sys-shortage-error-${Date.now()}`
        );
        return;
      }

      const loadingId = `loading-shortage-${timestamp}`;
      markStickToBottom();
      setIsChatLoading(true);
      setChatMessages((prev) => [
        ...prev,
        {
          id: loadingId,
          type: 'loading',
          text: t('Processing…', { defaultValue: 'Processing…' }),
        },
      ]);

      try {
        const payload = buildIssueShortageSuggestPayload({
          zoneId,
          locationId,
          draftCount: issueRows.length,
          draftItems: issueRows,
        });
        const data = await withMinLoaderDelay(
          Apiauth(payload, 'zone/suggest-issue-shortage').then((response) =>
            response?.code === 200 ? response.data : null
          )
        );

        setChatMessages((prev) =>
          prev.filter((message) => message.id !== loadingId)
        );

        if (!data) {
          appendStreamingSystemMessage(
            t('Unable to load shortage suggestions. Please try again.'),
            `sys-shortage-error-${Date.now()}`
          );
          return;
        }

        const products = normalizeAiChatProducts(data?.products);
        const reply = data?.message || '';

        if (products.length || reply.trim()) {
          appendProductSuggestionsMessage(reply, products, {
            showReply: Boolean(reply.trim()),
            intent: ISSUE_SHORTAGE_INTENT,
            streamReply: true,
          });
          return;
        }

        appendStreamingSystemMessage(
          t('No shortage suggestions found.'),
          `sys-shortage-empty-${Date.now()}`
        );
      } catch (err) {
        console.error(err, 'Issue shortage suggest API Error');
        setChatMessages((prev) =>
          prev.filter((message) => message.id !== loadingId)
        );
        appendStreamingSystemMessage(
          t('Unable to load shortage suggestions. Please try again.'),
          `sys-shortage-error-${Date.now()}`
        );
      } finally {
        setIsChatLoading(false);
      }
    },
    [
      appendProductSuggestionsMessage,
      appendStreamingSystemMessage,
      issueRows,
      locationId,
      markStickToBottom,
      onIssueShortageNo,
      onIssueShortageYes,
      t,
      zoneId,
    ]
  );

  const unmatchedPoProductIds = useMemo(
    () => getIssueUnmatchedPoProductIds(aiSuggestions, issueRows),
    [aiSuggestions, issueRows]
  );

  const isReplaceUnmatchedMessage = useCallback(
    (text) => {
      const normalized = text.trim().toLowerCase();
      const chipLabel = t('Replace unmatched items').toLowerCase();
      return (
        normalized === chipLabel ||
        normalized.includes('replace unmatched')
      );
    },
    [t]
  );

  const callZoneAiChat = useCallback(
    async (payload) => {
      if (!hasChatContext) return null;
      try {
        const response = await Apiauth(
          {
            ...payload,
            zone_id: zoneId,
            location_id: locationId,
            issue_id: issueId,
          },
          'zone/ai-chat'
        );
        if (response?.code !== 200) return null;
        return response.data;
      } catch (err) {
        console.error(err, 'Zone AI chat API Error');
        return null;
      }
    },
    [hasChatContext, issueId, locationId, zoneId]
  );

  const handleRefillSoldClick = useCallback(async () => {
    const sentText = t('Refill all sold items');
    const timestamp = Date.now();

    markStickToBottom();
    setChatMessages((prev) => [
      ...prev,
      {
        id: `sent-refill-sold-${timestamp}`,
        type: 'sent',
        text: sentText,
      },
    ]);

    if (hasSoldRefillSuggestions(aiSuggestions)) {
      const products = soldRefillItemsToChatProducts(aiSuggestions);
      const reply = buildRefillReplyFromSoldRefill(aiSuggestions);
      appendProductSuggestionsMessage(reply, products);
      return;
    }

    setChatMessages((prev) => [
      ...prev,
      {
        id: `loading-refill-${timestamp}`,
        type: 'loading',
        text: t('Processing…', { defaultValue: 'Processing…' }),
      },
    ]);

    if (!hasChatContext) {
      setChatMessages((prev) => [
        ...prev.filter((message) => message.type !== 'loading'),
        {
          id: `sys-refill-error-${timestamp}`,
          type: 'system',
          text: t('Unable to load refill suggestions. Issue context is missing.'),
        },
      ]);
      return;
    }

    setIsChatLoading(true);
    const data = await withMinLoaderDelay(
      callZoneAiChat({
        mode: 'preset',
        preset: 'refill_all_sold_items',
      })
    );
    setIsChatLoading(false);

    if (!data) {
      setChatMessages((prev) => [
        ...prev.filter((message) => message.type !== 'loading'),
        {
          id: `sys-refill-error-${timestamp}`,
          type: 'system',
          text: t('Unable to load refill suggestions. Please try again.'),
        },
      ]);
      return;
    }

    appendProductSuggestionsMessage(
      data?.reply || '',
      normalizeAiChatProducts(data?.products)
    );
  }, [
    aiSuggestions,
    appendProductSuggestionsMessage,
    callZoneAiChat,
    hasChatContext,
    t,
    markStickToBottom,
  ]);

  const handleReplaceUnmatchedClick = useCallback(async () => {
    const sentText = t('Replace unmatched items');
    const timestamp = Date.now();

    markStickToBottom();
    setChatMessages((prev) => [
      ...prev,
      {
        id: `sent-replace-unmatched-${timestamp}`,
        type: 'sent',
        text: sentText,
      },
    ]);

    if (!unmatchedPoProductIds.length) {
      setChatMessages((prev) => [
        ...prev,
        {
          id: `sys-replace-error-${timestamp}`,
          type: 'system',
          text: t('No unmatched items found in this issue.'),
        },
      ]);
      return;
    }

    setChatMessages((prev) => [
      ...prev,
      {
        id: `loading-replace-${timestamp}`,
        type: 'loading',
        text: t('Processing…', { defaultValue: 'Processing…' }),
      },
    ]);

    if (!hasChatContext) {
      setChatMessages((prev) => [
        ...prev.filter((message) => message.type !== 'loading'),
        {
          id: `sys-replace-error-${timestamp}`,
          type: 'system',
          text: t('Unable to load replacement suggestions. Issue context is missing.'),
        },
      ]);
      return;
    }

    setIsChatLoading(true);
    const data = await withMinLoaderDelay(
      callZoneAiChat({
        mode: 'preset',
        preset: 'replace_unmatched_items',
        unmatchedPoProductId: unmatchedPoProductIds,
      })
    );
    setIsChatLoading(false);

    if (!data) {
      setChatMessages((prev) => [
        ...prev.filter((message) => message.type !== 'loading'),
        {
          id: `sys-replace-error-${timestamp}`,
          type: 'system',
          text: t('Unable to load replacement suggestions. Please try again.'),
        },
      ]);
      return;
    }

    appendProductSuggestionsMessage(
      data?.reply ||
        t('Here are suggested replacements for unmatched items in this zone.', {
          zone: zoneLabel || t('this zone', { defaultValue: 'this zone' }),
        }),
      enrichReplaceUnmatchedProducts(
        normalizeAiChatProducts(data?.products),
        unmatchedPoProductIds
      ),
      { showReply: true, intent: 'replace_unmatched' }
    );
  }, [
    appendProductSuggestionsMessage,
    callZoneAiChat,
    hasChatContext,
    t,
    unmatchedPoProductIds,
    zoneLabel,
    markStickToBottom,
  ]);

  const handleReplaceCurrentItemClick = useCallback(() => {
    const sentText = t('Replace current item');
    const timestamp = Date.now();
    const loadingId = `loading-replace-current-${timestamp}`;

    clearPresetPromptTimer();
    markStickToBottom();
    setPendingPreset(REPLACE_CURRENT_ITEM_INTENT);
    setIsChatLoading(true);
    setChatMessages((prev) => [
      ...prev,
      {
        id: `sent-replace-current-${timestamp}`,
        type: 'sent',
        text: sentText,
      },
      {
        id: loadingId,
        type: 'loading',
        text: t('Processing…', { defaultValue: 'Processing…' }),
      },
    ]);

    presetPromptTimerRef.current = setTimeout(() => {
      setChatMessages((prev) => [
        ...prev.filter((message) => message.id !== loadingId),
        {
          id: `replace-current-prompt-${timestamp}`,
          type: 'replace_current_prompt',
        },
      ]);
      setIsChatLoading(false);
      presetPromptTimerRef.current = null;
    }, MIN_LOADER_DURATION_MS);
  }, [clearPresetPromptTimer, markStickToBottom, t]);

  const handleSuggestSimilarItemsClick = useCallback(() => {
    const sentText = t('Suggest similar items');
    const timestamp = Date.now();
    const loadingId = `loading-suggest-similar-${timestamp}`;

    clearPresetPromptTimer();
    markStickToBottom();
    setPendingPreset(SUGGEST_SIMILAR_ITEMS_PRESET);
    setIsChatLoading(true);
    setChatMessages((prev) => [
      ...prev,
      {
        id: `sent-suggest-similar-${timestamp}`,
        type: 'sent',
        text: sentText,
      },
      {
        id: loadingId,
        type: 'loading',
        text: t('Processing…', { defaultValue: 'Processing…' }),
      },
    ]);

    presetPromptTimerRef.current = setTimeout(() => {
      setChatMessages((prev) => [
        ...prev.filter((message) => message.id !== loadingId),
        {
          id: `suggest-similar-prompt-${timestamp}`,
          type: 'suggest_similar_prompt',
        },
      ]);
      setIsChatLoading(false);
      presetPromptTimerRef.current = null;
    }, MIN_LOADER_DURATION_MS);
  }, [clearPresetPromptTimer, markStickToBottom, t]);

  const handleRemoveUnmatchedClick = useCallback(() => {
    const started = onStartRemoveUnmatched?.();
    if (!started) return;

    const timestamp = Date.now();
    const systemMessageId = `sys-remove-unmatched-${timestamp}`;

    markStickToBottom();
    setChatMessages((prev) => [
      ...prev,
      {
        id: `sent-remove-unmatched-${timestamp}`,
        type: 'sent',
        text: t('Remove unmatched items'),
      },
      {
        id: systemMessageId,
        type: 'system',
        text: t('The unmatched items have been removed.'),
      },
    ]);
    setActiveUndoMessageId(systemMessageId);
    setUndoSecondsLeft(REMOVE_UNDO_SECONDS);
  }, [markStickToBottom, onStartRemoveUnmatched, t]);

  const handleUndoClick = () => {
    setUndoSecondsLeft(null);
    setActiveUndoMessageId(null);
    onUndoRemoveUnmatched?.();
  };

  const handleNewChat = () => {
    if (undoSecondsLeft !== null) {
      onUndoRemoveUnmatched?.();
    }
    clearPresetPromptTimer();
    clearAllMessageStreams();
    setIsChatLoading(false);
    setIsComposerSending(false);
    setUndoSecondsLeft(null);
    setActiveUndoMessageId(null);
    setPendingPreset(null);
    setInputValue('');
    setVisibleCount(INITIAL_VISIBLE_MESSAGES);
    pendingScrollRestoreRef.current = null;
    stickToBottomRef.current = true;
    if (loadOlderTimerRef.current) {
      clearTimeout(loadOlderTimerRef.current);
      loadOlderTimerRef.current = null;
    }
    setIsLoadingOlder(false);
    setChatMessages(
      showSuggestions ? [{ id: AI_SUGGESTIONS_MSG_ID, type: 'assistant' }] : []
    );
  };

  const handleToggle = () => {
    if (!isChatEnabled) return;
    setIsOpen((open) => !open);
  };

  const handleClose = () => {
    if (undoSecondsLeft !== null) {
      onUndoRemoveUnmatched?.();
    }
    setUndoSecondsLeft(null);
    setActiveUndoMessageId(null);
    setIsOpen(false);
  };

  const handleChipClick = (chip) => {
    if (isChatLoading) return;
    if (chip.id === 'remove_unmatched') {
      handleRemoveUnmatchedClick();
      return;
    }
    if (chip.id === 'refill_sold') {
      handleRefillSoldClick();
      return;
    }
    if (chip.id === 'replace_unmatched') {
      handleReplaceUnmatchedClick();
      return;
    }
    if (chip.id === 'replace') {
      handleReplaceCurrentItemClick();
      return;
    }
    if (chip.id === 'suggest') {
      handleSuggestSimilarItemsClick();
      return;
    }
    setInputValue(t(chip.labelKey));
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isChatLoading || isComposerSending) return;

    markStickToBottom();
    const timestamp = Date.now();
    const activePreset = pendingPreset;

    setChatMessages((prev) => [
      ...prev,
      { id: `sent-${timestamp}`, type: 'sent', text },
      {
        id: `loading-chat-${timestamp}`,
        type: 'loading',
        text: t('Processing…', { defaultValue: 'Processing…' }),
      },
    ]);
    setInputValue('');

    if (!hasChatContext) {
      setChatMessages((prev) => [
        ...prev.filter((message) => message.type !== 'loading'),
        {
          id: `sys-chat-error-${timestamp}`,
          type: 'system',
          text: t('Unable to send message. Issue context is missing.'),
        },
      ]);
      return;
    }

    if (
      activePreset === REPLACE_CURRENT_ITEM_INTENT ||
      activePreset === SUGGEST_SIMILAR_ITEMS_PRESET
    ) {
      setPendingPreset(null);
    }

    let payload;
    if (activePreset === REPLACE_CURRENT_ITEM_INTENT) {
      payload = {
        mode: 'preset',
        preset: REPLACE_CURRENT_ITEM_INTENT,
        message: text,
      };
    } else if (activePreset === SUGGEST_SIMILAR_ITEMS_PRESET) {
      payload = {
        mode: 'preset',
        preset: SUGGEST_SIMILAR_ITEMS_PRESET,
        message: text,
      };
    } else {
      payload = {
        mode: 'free',
        message: text,
      };
      if (isReplaceUnmatchedMessage(text) && unmatchedPoProductIds.length) {
        payload.unmatchedPoProductId = unmatchedPoProductIds;
      }
    }

    setIsComposerSending(true);
    setIsChatLoading(true);

    try {
      const data = await withMinLoaderDelay(callZoneAiChat(payload));

      if (!data) {
        setChatMessages((prev) => [
          ...prev.filter((message) => message.type !== 'loading'),
          {
            id: `sys-chat-error-${timestamp}`,
            type: 'system',
            text: t('Unable to get a response. Please try again.'),
          },
        ]);
        return;
      }

      const products = normalizeAiChatProducts(data?.products);
      if (products.length > 0) {
        const intent = resolveChatProductIntent({
          activePreset,
          messageText: text,
          data,
          products,
          unmatchedPoProductIds,
          isReplaceUnmatchedMessage,
        });
        const normalizedProducts =
          intent === 'replace_unmatched'
            ? enrichReplaceUnmatchedProducts(products, unmatchedPoProductIds)
            : intent === REPLACE_CURRENT_ITEM_INTENT
              ? enrichReplaceCurrentItemProducts(products, data)
              : products;
        appendProductSuggestionsMessage(data?.reply || '', normalizedProducts, {
          showReply: true,
          intent,
          streamReply: true,
        });
        return;
      }

      appendStreamingSystemMessage(
        data?.reply || t('No response received.'),
        `sys-reply-${timestamp}`
      );
    } finally {
      setIsChatLoading(false);
      setIsComposerSending(false);
    }
  };

  const isProductAdded = useCallback(
    (product) =>
      isChatProductShowingAsAdded(product, issueRows, addedProductKeys),
    [addedProductKeys, issueRows]
  );

  const handleProductAdd = useCallback(
    async (product, intent) => {
      const key = getChatProductKey(product);
      if (!key || isProductAdded(product) || addingProductKey) return;

      setAddingProductKey(key);
      try {
        const success = await onAddChatProduct?.(product, { intent });
        if (success !== false) {
          setAddedProductKeys((prev) => new Set([...prev, key]));
        }
      } finally {
        setAddingProductKey(null);
      }
    },
    [addingProductKey, isProductAdded, onAddChatProduct]
  );

  const greetingName = userName || t('User', { defaultValue: 'User' });
  const zoneName =
    zoneLabel || t('this zone', { defaultValue: 'this zone' });
  const resolvedPanelTitle =
    panelTitle || t('Chat with AI', { defaultValue: 'Chat with AI' });
  const showNotificationDot = hasNotification && !panelMounted;

  const showComposerHint = PRESET_COMPOSER_HINT_PRESETS.has(pendingPreset);
  const composerHintText = t("Type item's information.", {
    defaultValue: "Type item's information.",
  });
  const hasComposerText = Boolean(inputValue.trim());
  const sendButtonState = isComposerSending
    ? 'loading'
    : hasComposerText
      ? 'active'
      : 'idle';

  const slashMenuState = useMemo(
    () => getSlashMenuState(inputValue, inputSelection),
    [inputValue, inputSelection]
  );
  const slashMenuOptions = useMemo(
    () => filterSlashMenuOptions(ZONE_CHAT_SEARCH_FIELDS, slashMenuState?.query),
    [slashMenuState]
  );
  const showSlashMenu = Boolean(slashMenuState && slashMenuOptions.length);

  const fieldValueMenuState = useMemo(() => {
    if (slashMenuState) return null;
    return getSearchFieldValueMenuState(inputValue, inputSelection);
  }, [inputSelection, inputValue, slashMenuState]);

  const fieldValueMenuOptions = useMemo(
    () =>
      fieldValueMenuState
        ? getIssueFieldValueSuggestions(
            fieldValueMenuState.fieldId,
            issueRows,
            fieldValueMenuState.query
          )
        : [],
    [fieldValueMenuState, issueRows]
  );

  const fieldValueMenuCounts = useMemo(
    () =>
      fieldValueMenuState
        ? getIssueFieldValueCounts(fieldValueMenuState.fieldId, issueRows)
        : {},
    [fieldValueMenuState, issueRows]
  );

  const fieldValueMenuAriaLabel = useMemo(() => {
    switch (fieldValueMenuState?.fieldId) {
      case 'sku':
        return t('SKUs in this issue');
      case 'collection':
        return t('Collections in this issue');
      case 'item_type':
      default:
        return t('Item types in this issue');
    }
  }, [fieldValueMenuState?.fieldId, t]);

  const showFieldValueMenu = Boolean(
    !fieldValueMenuDismissed &&
      isIssueFieldValuePickerField(fieldValueMenuState?.fieldId) &&
      fieldValueMenuOptions.length
  );

  const syncInputSelection = useCallback(() => {
    const cursorPos = textareaRef.current?.selectionStart;
    if (typeof cursorPos === 'number') {
      setInputSelection(cursorPos);
    }
  }, []);

  const handleComposerInputChange = (event) => {
    const nextValue = event.target.value;
    const nextSelection =
      event.target.selectionStart ?? event.target.value.length;

    setInputValue(nextValue);
    setInputSelection(nextSelection);
    setSlashMenuHighlightIndex(0);
    setFieldValueMenuHighlightIndex(0);

    const nextFieldState = getSearchFieldValueMenuState(nextValue, nextSelection);
    if (
      !nextFieldState ||
      !isIssueFieldValuePickerField(nextFieldState.fieldId)
    ) {
      setFieldValueMenuDismissed(false);
    } else if (!String(nextFieldState.query).trim()) {
      setFieldValueMenuDismissed(false);
    }
  };

  const applyFieldValueSelection = useCallback(
    (selectedValue) => {
      if (!fieldValueMenuState || !selectedValue) return;

      const { nextValue, nextCursorPos } = buildInputWithFieldValue(
        inputValue,
        inputSelection,
        fieldValueMenuState,
        selectedValue
      );

      setInputValue(nextValue);
      setInputSelection(nextCursorPos);
      setFieldValueMenuHighlightIndex(0);
      setFieldValueMenuDismissed(true);

      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.focus();
        textarea.setSelectionRange(nextCursorPos, nextCursorPos);
      });
    },
    [fieldValueMenuState, inputSelection, inputValue]
  );

  const applySlashFieldSelection = useCallback(
    (fieldId) => {
      if (!slashMenuState) return;

      const { nextValue, nextCursorPos } = buildInputWithSlashField(
        inputValue,
        inputSelection,
        slashMenuState.slashIndex,
        fieldId
      );

      setInputValue(nextValue);
      setInputSelection(nextCursorPos);
      setSlashMenuHighlightIndex(0);
      setFieldValueMenuHighlightIndex(0);
      setFieldValueMenuDismissed(!isIssueFieldValuePickerField(fieldId));

      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.focus();
        textarea.setSelectionRange(nextCursorPos, nextCursorPos);
      });
    },
    [inputSelection, inputValue, slashMenuState]
  );

  const handleComposerKeyDown = (event) => {
    if (showSlashMenu) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSlashMenuHighlightIndex(
          (prev) => (prev + 1) % slashMenuOptions.length
        );
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSlashMenuHighlightIndex(
          (prev) => (prev - 1 + slashMenuOptions.length) % slashMenuOptions.length
        );
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const selectedOption =
          slashMenuOptions[slashMenuHighlightIndex] || slashMenuOptions[0];
        if (selectedOption?.id) {
          applySlashFieldSelection(selectedOption.id);
        }
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        const selectedOption =
          slashMenuOptions[slashMenuHighlightIndex] || slashMenuOptions[0];
        if (selectedOption?.id) {
          applySlashFieldSelection(selectedOption.id);
        }
        return;
      }
    }

    if (showFieldValueMenu) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setFieldValueMenuHighlightIndex(
          (prev) => (prev + 1) % fieldValueMenuOptions.length
        );
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setFieldValueMenuHighlightIndex(
          (prev) =>
            (prev - 1 + fieldValueMenuOptions.length) %
            fieldValueMenuOptions.length
        );
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const selectedValue =
          fieldValueMenuOptions[fieldValueMenuHighlightIndex] ||
          fieldValueMenuOptions[0];
        if (selectedValue) {
          applyFieldValueSelection(selectedValue);
        }
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        const selectedValue =
          fieldValueMenuOptions[fieldValueMenuHighlightIndex] ||
          fieldValueMenuOptions[0];
        if (selectedValue) {
          applyFieldValueSelection(selectedValue);
        }
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const renderMessage = (message) => {
    if (message.type === 'assistant') {
      const suggestionData = message.aiSuggestions ?? aiSuggestions;
      const messageSoldLines = getSoldRefillLines(suggestionData);
      const messageUnmatchedLines = getUnmatchedLines(suggestionData);
      const messageSoldStatus =
        messageSoldLines.length > 0
          ? ''
          : getSoldRefillStatusMessage(suggestionData);
      const messageUnmatchedStatus =
        messageUnmatchedLines.length > 0
          ? ''
          : getUnmatchedReplaceStatusMessage(suggestionData);
      const messageZoneName =
        message.zoneName || zoneName;

      return (
        <div
          key={message.id}
          className="zone-chat-widget__message zone-chat-widget__message--assistant"
        >
          <AssistantSuggestionsContent
            zoneName={messageZoneName}
            soldLines={messageSoldLines}
            unmatchedLines={messageUnmatchedLines}
            soldStatusMessage={messageSoldStatus}
            unmatchedStatusMessage={messageUnmatchedStatus}
            t={t}
          />
        </div>
      );
    }

    if (message.type === 'sent') {
      return (
        <div
          key={message.id}
          className="zone-chat-widget__message zone-chat-widget__message--sent"
        >
          {message.text}
        </div>
      );
    }

    if (message.type === 'loading') {
      return <ChatLoadingMessage key={message.id} text={message.text} />;
    }

    if (message.type === 'replace_current_prompt') {
      return (
        <div
          key={message.id}
          className="zone-chat-widget__message zone-chat-widget__message--assistant zone-chat-widget__message--refill"
        >
          <ReplaceCurrentItemPromptContent t={t} />
        </div>
      );
    }

    if (message.type === 'suggest_similar_prompt') {
      return (
        <div
          key={message.id}
          className="zone-chat-widget__message zone-chat-widget__message--assistant zone-chat-widget__message--refill"
        >
          <SuggestSimilarItemsPromptContent t={t} />
        </div>
      );
    }

    if (message.type === 'issue_shortage_prompt') {
      return (
        <div
          key={message.id}
          className="zone-chat-widget__message zone-chat-widget__message--assistant zone-chat-widget__message--refill"
        >
          <IssueShortagePromptContent
            alertData={message.alertData}
            resolved={message.resolved}
            onYes={() => handleIssueShortageChoice(message.id, 'yes')}
            onNo={() => handleIssueShortageChoice(message.id, 'no')}
            t={t}
          />
        </div>
      );
    }

    if (message.type === 'refill_products') {
      const isReplaceIntent = isReplaceProductIntent(message.intent);
      const addActionTitle = isReplaceIntent
        ? t('Click to replace', { defaultValue: 'Click to replace' })
        : t('Add to issue');
      const showReplyText =
        message.showReply && (message.reply || message.isStreamingReply);
      const showProductCards =
        message.showProducts !== false && message.products?.length > 0;

      return (
        <div
          key={message.id}
          className="zone-chat-widget__message zone-chat-widget__message--assistant zone-chat-widget__message--refill"
        >
          {showReplyText ? (
            <p className="zone-chat-widget__reply-text">
              {message.reply}
              {message.isStreamingReply ? <ChatStreamCursor /> : null}
            </p>
          ) : null}
          {!message.showReply &&
          message.reply &&
          !message.products?.length &&
          !message.isStreamingReply ? (
            <p className="zone-chat-widget__reply-text">{message.reply}</p>
          ) : null}
          {showProductCards ? (
            <div className="zone-chat-widget__product-cards">
              {message.products.map((product) => (
                <RefillProductCard
                  key={`${product.id}-${product.SKU}`}
                  product={product}
                  isAdded={isProductAdded(product)}
                  isAdding={addingProductKey === getChatProductKey(product)}
                  onAdd={(p) => handleProductAdd(p, message.intent)}
                  actionTitle={addActionTitle}
                  isReplaceAction={isReplaceIntent}
                  t={t}
                />
              ))}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div
        key={message.id}
        className="zone-chat-widget__message zone-chat-widget__message--system"
      >
        <p>
          {message.text}
          {message.isStreaming ? <ChatStreamCursor /> : null}
        </p>
        {message.id === activeUndoMessageId && undoSecondsLeft !== null ? (
          <button
            type="button"
            className="zone-chat-widget__undo-btn"
            onClick={handleUndoClick}
          >
            {t('Undo ({{seconds}}s)', { seconds: undoSecondsLeft })}
          </button>
        ) : null}
      </div>
    );
  };

  const renderChip = (chip) => {
    const Icon = CHIP_ICONS[chip.id] || ChipIconReplace;
    return (
      <button
        key={chip.id}
        type="button"
        className={`zone-chat-widget__chip${
          chip.highlighted ? ' zone-chat-widget__chip--highlighted' : ''
        }`}
        onClick={() => handleChipClick(chip)}
        disabled={isChatLoading}
      >
        <Icon />
        <span>{t(chip.labelKey)}</span>
        {chip.showAiBadge ? <ChipAiBadge /> : null}
      </button>
    );
  };

  return (
    <div className="zone-chat-widget">
      {panelMounted && (
        <div
          className={`zone-chat-widget__panel${
            panelVisible ? ' zone-chat-widget__panel--visible' : ''
          }`}
          role="dialog"
          aria-label={resolvedPanelTitle}
          aria-hidden={!panelVisible}
          onTransitionEnd={handlePanelTransitionEnd}
        >
          <div
            className={`zone-chat-widget__panel-inner${
              hideComposerAndPresets
                ? ' zone-chat-widget__panel-inner--readonly'
                : ''
            }`}
          >
            <header className="zone-chat-widget__header">
              <div className="zone-chat-widget__brand">
                <img
                  src={aibirdeyeIcon}
                  alt=""
                  className="zone-chat-widget__brand-icon"
                />
                <span>{resolvedPanelTitle}</span>
              </div>
              <div className="zone-chat-widget__header-actions">
                {!disableChatSessionControls ? (
                  <>
                    <button
                      type="button"
                      className="zone-chat-widget__icon-btn"
                      aria-label={t('New chat')}
                      onClick={handleNewChat}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path d="M12 5V19M5 12H19" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="zone-chat-widget__icon-btn"
                      aria-label={t('Chat history')}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path d="M11 5.25H13" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M11 12H13" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M11 18.75H13" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="zone-chat-widget__icon-btn"
                  onClick={handleClose}
                  aria-label={t('Close')}
                >
                  <CloseIcon color="#606060" width="24" height="24" />
                </button>
              </div>
            </header>

            <div
              ref={bodyRef}
              onScroll={handleBodyScroll}
              onWheel={handleBodyWheel}
              className={`zone-chat-widget__body${
                hasTranscript ? ' zone-chat-widget__body--chat' : ''
              }`}
            >
              {hasTranscript ? (
                <div className="zone-chat-widget__transcript">
                  {isLoadingOlder && !disableChatSessionControls ? (
                    <div
                      className="zone-chat-widget__load-older"
                      role="status"
                      aria-live="polite"
                    >
                      <span
                        className="zone-chat-widget__loading-spinner"
                        aria-hidden
                      />
                      <span className="zone-chat-widget__loading-text">
                        {t('Loading previous messages…', {
                          defaultValue: 'Loading previous messages…',
                        })}
                      </span>
                    </div>
                  ) : null}
                  {visibleMessages.map((message) => renderMessage(message))}
                  <div ref={transcriptEndRef} className="zone-chat-widget__transcript-anchor" />
                </div>
              ) : showGreeting ? (
                <div className="zone-chat-widget__greeting">
                  <p className="zone-chat-widget__greeting-hello">
                    {t('Hello')}, {greetingName}
                  </p>
                  <p className="zone-chat-widget__greeting-sub">
                    {t('How can I help you today?')}
                  </p>
                </div>
              ) : null}
            </div>

            {showChips ? (
              <div className="zone-chat-widget__chips">
                {actionChips.map((chip) => renderChip(chip))}
              </div>
            ) : null}

            {!hideComposerAndPresets ? (
            <div
              className={`zone-chat-widget__composer${
                showComposerHint ? ' zone-chat-widget__composer--with-hint' : ''
              }`}
            >
              {showComposerHint ? (
                <div className="zone-chat-widget__composer-hint">
                  {composerHintText}
                </div>
              ) : null}
              {showFieldValueMenu ? (
                <div
                  className="zone-chat-widget__slash-menu zone-chat-widget__slash-menu--values"
                  role="listbox"
                  aria-label={fieldValueMenuAriaLabel}
                >
                  {fieldValueMenuOptions.map((option, index) => {
                    const optionCount =
                      fieldValueMenuCounts[String(option).toLowerCase()] || 0;

                    return (
                      <button
                        key={option}
                        type="button"
                        role="option"
                        aria-selected={index === fieldValueMenuHighlightIndex}
                        className={`zone-chat-widget__slash-menu-item${
                          index === fieldValueMenuHighlightIndex
                            ? ' zone-chat-widget__slash-menu-item--active'
                            : ''
                        }`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => applyFieldValueSelection(option)}
                      >
                        <span className="zone-chat-widget__slash-menu-label">
                          {option}
                        </span>
                        {optionCount > 0 ? (
                          <span className="zone-chat-widget__slash-menu-count">
                            {optionCount}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {showSlashMenu ? (
                <div
                  className="zone-chat-widget__slash-menu"
                  role="listbox"
                  aria-label={t('Search field')}
                >
                  {slashMenuOptions.map((option, index) => (
                    <button
                      key={option.id}
                      type="button"
                      role="option"
                      aria-selected={index === slashMenuHighlightIndex}
                      className={`zone-chat-widget__slash-menu-item${
                        index === slashMenuHighlightIndex
                          ? ' zone-chat-widget__slash-menu-item--active'
                          : ''
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => applySlashFieldSelection(option.id)}
                    >
                      <span className="zone-chat-widget__slash-menu-label">
                        {t(option.labelKey, {
                          defaultValue: option.defaultLabel,
                        })}
                      </span>
                      <span className="zone-chat-widget__slash-menu-id">
                        {option.id}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
              <textarea
                ref={textareaRef}
                className="zone-chat-widget__input"
                placeholder={
                  showComposerHint ? composerHintText : t('What I can help you?')
                }
                value={inputValue}
                onChange={handleComposerInputChange}
                onKeyDown={handleComposerKeyDown}
                onClick={syncInputSelection}
                onKeyUp={syncInputSelection}
                onSelect={syncInputSelection}
                rows={2}
                aria-label={t('Message')}
              />
              <div className="zone-chat-widget__composer-actions">
                <button
                  type="button"
                  className="zone-chat-widget__round-btn"
                  aria-label={t('Voice input')}
                >
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
                    <path
                      d="M11 14.6667C12.933 14.6667 14.5 13.0997 14.5 11.1667V6.41667C14.5 4.48367 12.933 2.91667 11 2.91667C9.067 2.91667 7.5 4.48367 7.5 6.41667V11.1667C7.5 13.0997 9.067 14.6667 11 14.6667Z"
                      stroke="#A4A4A4"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5.5 11.1667H4.58333C4.58333 14.1753 7.02467 16.6167 10.0333 16.6167H11.9667C14.9753 16.6167 17.4167 14.1753 17.4167 11.1667H16.5"
                      stroke="#A4A4A4"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M11 16.6167V19.25" stroke="#A4A4A4" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`zone-chat-widget__round-btn zone-chat-widget__round-btn--send${
                    sendButtonState === 'active'
                      ? ' zone-chat-widget__round-btn--send-active'
                      : ''
                  }${
                    sendButtonState === 'loading'
                      ? ' zone-chat-widget__round-btn--send-loading'
                      : ''
                  }`}
                  onClick={handleSend}
                  aria-label={
                    sendButtonState === 'loading'
                      ? t('Processing…', { defaultValue: 'Processing…' })
                      : t('Send')
                  }
                  disabled={sendButtonState !== 'active'}
                >
                  {sendButtonState === 'loading' ? (
                    <ComposerStopIcon />
                  ) : (
                    <ComposerSendIcon />
                  )}
                </button>
              </div>
            </div>
            ) : null}
          </div>
        </div>
      )}
      <button
        type="button"
        className={`zone-chat-widget__toggle${
          panelMounted ? ' zone-chat-widget__toggle--covered' : ''
        }${!isChatEnabled ? ' zone-chat-widget__toggle--disabled' : ''}`}
        onClick={handleToggle}
        disabled={!isChatEnabled}
        aria-label={
          !isChatEnabled
            ? t('Click Edit on the issue to use AI chat', {
                defaultValue: 'Click Edit on the issue to use AI chat',
              })
            : showNotificationDot
              ? t('Open AI chat to see the notification', {
                  defaultValue: 'Open AI chat to see the notification',
                })
              : t('Open assistant', { defaultValue: 'Open assistant' })
        }
        aria-expanded={panelMounted}
        aria-disabled={!isChatEnabled}
        aria-hidden={panelMounted}
        tabIndex={panelMounted || !isChatEnabled ? -1 : 0}
      >
        <img src={aibirdeyeIcon} alt="" aria-hidden />
        {showNotificationDot ? (
          <span className="zone-chat-widget__notification-dot" aria-hidden />
        ) : null}
      </button>
    </div>
  );
}
